"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { Users, Award, BookOpen, TrendingUp } from "lucide-react";

interface StatItem {
  icon: React.ReactNode;
  value: number;
  suffix: string;
  prefix: string;
  label: string;
  hoverColor: string;
  hoverBg: string;
  hoverGlow: string;
  hoverBorder: string;
  hoverLine: string;
}

// Cores fixas por posição (não são configuráveis no admin)
const STAT_PALETTE = [
  {
    icon: <Users className="w-8 h-8" />,
    hoverColor: "group-hover:text-cyan-400",
    hoverBg: "group-hover:bg-cyan-500",
    hoverGlow: "group-hover:bg-cyan-500/30",
    hoverBorder: "group-hover:border-cyan-500/50",
    hoverLine: "group-hover:bg-cyan-500",
  },
  {
    icon: <Award className="w-8 h-8" />,
    hoverColor: "group-hover:text-amber-400",
    hoverBg: "group-hover:bg-amber-500",
    hoverGlow: "group-hover:bg-amber-500/30",
    hoverBorder: "group-hover:border-amber-500/50",
    hoverLine: "group-hover:bg-amber-500",
  },
  {
    icon: <BookOpen className="w-8 h-8" />,
    hoverColor: "group-hover:text-emerald-400",
    hoverBg: "group-hover:bg-emerald-500",
    hoverGlow: "group-hover:bg-emerald-500/30",
    hoverBorder: "group-hover:border-emerald-500/50",
    hoverLine: "group-hover:bg-emerald-500",
  },
  {
    icon: <TrendingUp className="w-8 h-8" />,
    hoverColor: "group-hover:text-purple-400",
    hoverBg: "group-hover:bg-purple-500",
    hoverGlow: "group-hover:bg-purple-500/30",
    hoverBorder: "group-hover:border-purple-500/50",
    hoverLine: "group-hover:bg-purple-500",
  },
];

const DEFAULT_STATS: StatItem[] = [
  { ...STAT_PALETTE[0], value: 5000, prefix: "+", suffix: "", label: "Alunos Formados" },
  { ...STAT_PALETTE[1], value: 15,   prefix: "+", suffix: "", label: "Anos de Experiência" },
  { ...STAT_PALETTE[2], value: 50,   prefix: "+", suffix: "", label: "Cursos Disponíveis" },
  { ...STAT_PALETTE[3], value: 98,   prefix: "",  suffix: "%", label: "de Aprovação" },
];

function useCountUp(end: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    let startTime: number | null = null;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, start]);

  return count;
}

function StatCard({ stat, isVisible }: { stat: StatItem; isVisible: boolean }) {
  const count = useCountUp(stat.value, 2500, isVisible);

  const formatNumber = (num: number) => {
    return num.toLocaleString("pt-BR");
  };

  return (
    <div className="relative group transition-transform duration-300 hover:scale-105 hover:-translate-y-2 cursor-pointer">
      {/* Background glow effect */}
      <div className={`absolute -inset-1 bg-gray-600 ${stat.hoverGlow} rounded-2xl blur-lg opacity-20 group-hover:opacity-40 transition-all duration-500`} />

      <div className={`relative bg-gray-900 rounded-2xl p-8 border border-gray-800 ${stat.hoverBorder} transition-all duration-300`}>
        {/* Icon */}
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gray-700 ${stat.hoverBg} mb-6 shadow-lg transition-all duration-300`}>
          <div className="text-gray-300 group-hover:text-white transition-colors duration-300">
            {stat.icon}
          </div>
        </div>

        {/* Number */}
        <div className="mb-2">
          <span className={`text-4xl md:text-5xl font-black text-white ${stat.hoverColor} transition-colors duration-300`}>
            {stat.prefix}{formatNumber(count)}{stat.suffix}
          </span>
        </div>

        {/* Label */}
        <p className="text-gray-400 text-lg font-medium">
          {stat.label}
        </p>

        {/* Decorative line */}
        <div className={`absolute bottom-0 left-8 right-8 h-1 bg-gray-600 ${stat.hoverLine} rounded-full opacity-50 group-hover:opacity-100 transition-all duration-300`} />
      </div>
    </div>
  );
}

export default function StatsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [stats, setStats] = useState<StatItem[]>(DEFAULT_STATS);
  const sectionRef = useRef<HTMLElement>(null);

  // Carrega valores dinâmicos do admin (com fallback no default)
  useEffect(() => {
    (async () => {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "http://localhost:5000";
        const res = await fetch(`${backendUrl}/api/website/public/logos`);
        if (!res.ok) return;
        const json = await res.json();
        if (!json?.success || !json.data) return;
        setStats(DEFAULT_STATS.map((d, i) => {
          const idx = i + 1;
          const rawValue = json.data[`stats_${idx}_value`];
          const value = rawValue !== undefined && rawValue !== null && rawValue !== ""
            ? Number(rawValue) || d.value
            : d.value;
          return {
            ...d,
            value,
            prefix: json.data[`stats_${idx}_prefix`] ?? d.prefix,
            suffix: json.data[`stats_${idx}_suffix`] ?? d.suffix,
            label:  json.data[`stats_${idx}_label`]  ?? d.label,
          };
        }));
      } catch {
        // mantém defaults em caso de falha
      }
    })();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.2,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-12 md:py-16 bg-gray-950 overflow-hidden"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      {/* Gradient orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-gray-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gray-600/20 rounded-full blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-4">
        {/* Header */}
        <div className={`text-center mb-8 md:mb-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block text-gray-400 font-semibold text-sm uppercase tracking-wider mb-2">
            Nossos Números
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Resultados que <span className="text-gray-400">Falam</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Anos de dedicação formando profissionais qualificados para o <span className="text-[#2AABDE] font-semibold">mercado offshore</span>
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`transition-all duration-700 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <StatCard stat={stat} isVisible={isVisible} />
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-10 md:mt-12 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-gray-500 mb-4">
            Junte-se a milhares de profissionais que transformaram suas carreiras
          </p>
          <Link
            href="/cursos"
            className="inline-block px-8 py-4 bg-gray-700 text-white font-semibold rounded-full hover:bg-gray-600 hover:shadow-lg hover:shadow-gray-900/30 transition-all duration-300 hover:-translate-y-1"
          >
            Comece Agora
          </Link>
        </div>
      </div>
    </section>
  );
}
